import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Sprout, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PROPERTY_TYPES } from "../lib/propertyTypes";

/**
 * "Convert land to income property" — reclassifies a `land` parcel as a
 * developed/income asset once construction or entitlement work is complete.
 * Preserves the existing purchase history (purchase_price, purchase_date,
 * address, lat/lng) so the chain of cost basis is intact, but allows the
 * user to:
 *   - pick a new property_type (any non-land type)
 *   - bump units (now that there's a building)
 *   - bump market value (developed land is usually worth more)
 *
 * Server: reuses PUT /api/properties/{pid} (which validates against
 * PropertyCreate, so all fields must be sent — we hydrate the rest from
 * the existing record).
 *
 * Props:
 *   - property      the existing land property record (or null = closed)
 *   - onOpenChange(bool)
 *   - onConverted() callback fired after success so parent reloads
 */
export default function ConvertLandToIncomeDialog({
  property, onOpenChange, onConverted,
}) {
  const open = !!property;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_type: "office",
    units: 1,
    market_value: 0,
    notes_addendum: "",
  });

  useEffect(() => {
    if (!open || !property) return;
    setForm({
      property_type: "office",
      units: Math.max(1, Number(property.units) || 1),
      market_value: Number(property.market_value) || 0,
      notes_addendum: "",
    });
  }, [open, property]);

  // Hide land + other from the new-type list (other is allowed but rarely
  // useful as a "graduation" target). Keep alphabetical via existing catalog
  // ordering.
  const targetTypes = PROPERTY_TYPES.filter(
    (t) => t.value !== "land" && t.value !== "other",
  );

  const submit = async (e) => {
    e.preventDefault();
    if (form.property_type === "land") {
      toast.error("Pick a non-land type to convert into");
      return;
    }
    if (Number(form.units) < 1) {
      toast.error("Units must be at least 1");
      return;
    }
    setSaving(true);
    try {
      // PUT /properties/{pid} validates against PropertyCreate, so we MUST
      // send the full payload — hydrate everything else from the existing
      // record so we don't accidentally null fields out.
      const existingNotes = (property.notes || "").trim();
      const addendum = form.notes_addendum.trim();
      const stamp = `[Converted from Land → ${form.property_type} on ${new Date().toISOString().slice(0, 10)}]`;
      const newNotes = [existingNotes, stamp, addendum].filter(Boolean).join("\n");

      const payload = {
        name: property.name,
        address: property.address,
        property_type: form.property_type,
        units: Number(form.units),
        purchase_price: Number(property.purchase_price) || 0,
        purchase_date: property.purchase_date || null,
        market_value: Number(form.market_value) || 0,
        annual_taxes: Number(property.annual_taxes) || 0,
        annual_insurance: Number(property.annual_insurance) || 0,
        notes: newNotes,
        lat: property.lat ?? null,
        lng: property.lng ?? null,
        formatted_address: property.formatted_address || null,
        place_id: property.place_id || null,
      };
      await api.put(`/properties/${property.id}`, payload);
      toast.success(`${property.name} graduated to ${form.property_type.replace("_", " ")}`);
      onConverted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Conversion failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="convert-land-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Sprout className="w-5 h-5 text-emerald-600" /> Convert land to income property
          </DialogTitle>
          {property && (
            <p className="text-sm text-slate-500 mt-1">
              Reclassify <span className="font-semibold text-slate-700">{property.name}</span> now
              that the parcel has been developed. Purchase history (cost basis +
              purchase date) is preserved.
            </p>
          )}
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 pt-2">
          <div className="flex items-center justify-center gap-3 text-xs font-mono uppercase tracking-widest text-slate-500 py-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-700">
              🌿 Land
            </span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-slate-900 text-white">
              {targetTypes.find((t) => t.value === form.property_type)?.emoji || "📦"}{" "}
              {targetTypes.find((t) => t.value === form.property_type)?.label || form.property_type}
            </span>
          </div>

          <Field label="New property type">
            <select
              required
              className="input-clean w-full"
              data-testid="cli-type-select"
              value={form.property_type}
              onChange={(e) => setForm({ ...form, property_type: e.target.value })}
            >
              {targetTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.emoji} {t.label}
                  {t.capMin != null ? `  ·  ${t.capMin}–${t.capMax}% cap` : ""}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Units">
              <input
                required
                type="number"
                min="1"
                className="input-clean w-full"
                data-testid="cli-units-input"
                value={form.units}
                onChange={(e) => setForm({ ...form, units: e.target.value })}
              />
            </Field>
            <Field label="New market value (USD)">
              <input
                required
                type="number"
                min="0"
                step="0.01"
                className="input-clean w-full"
                data-testid="cli-value-input"
                value={form.market_value}
                onChange={(e) => setForm({ ...form, market_value: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Notes (optional)">
            <textarea
              rows={2}
              className="input-clean w-full"
              data-testid="cli-notes-input"
              placeholder="e.g. CO issued, building stabilized, etc."
              value={form.notes_addendum}
              onChange={(e) => setForm({ ...form, notes_addendum: e.target.value })}
            />
            <div className="text-[11px] text-slate-500 mt-1">
              A "Converted from Land" timestamp is automatically appended to the
              property's notes for audit purposes.
            </div>
          </Field>

          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline-dark"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-dark disabled:opacity-60"
              data-testid="cli-submit-btn"
              disabled={saving}
            >
              {saving ? "Converting…" : "Convert"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
